import { createSignal } from "solid-js";
import type { QuotaScope, QuotaCap } from "../api/types";

/** Which budget the refusal was charged against (FLOW-910). Declared in `api/types`. */
export type { QuotaScope };
/** Which tenant window a `QUOTA_EXHAUSTED` refusal tripped (FLOW-928). Declared in `api/types`. */
export type { QuotaCap };

/**
 * Module-level for the same reason as chatDrawerStore: AppShell is mounted
 * per route, so a component-local signal would reset on navigation.
 */
export interface QuotaPaywall {
  resumesAt: Date | null;
  scope: QuotaScope;
  cap: QuotaCap | null;
}

const [quotaPaywall, setQuotaPaywall] = createSignal<QuotaPaywall | null>(null);
const [quotaRequestOpen, setQuotaRequestOpen] = createSignal(false);

/**
 * Ignored while a paywall is already showing (D4), or while the
 * request-more-quota form is open — that form is reached only by dismissing
 * a paywall, and a second refusal arriving while it's open must not stack a
 * new modal on top of it. `now` is injectable for tests.
 */
export function showQuotaPaywall(
  retryAfterSecs: number | undefined,
  scope: QuotaScope,
  cap: QuotaCap | null,
  now: number = Date.now(),
): void {
  if (quotaPaywall() || quotaRequestOpen()) return;
  setQuotaPaywall({
    resumesAt:
      retryAfterSecs === undefined
        ? null
        : new Date(now + retryAfterSecs * 1000),
    scope,
    cap,
  });
}

export function dismissQuotaPaywall(): void {
  setQuotaPaywall(null);
}

/** Closes the paywall and opens the request-more-quota form (D5). FLOW-794 reuses this. */
export function openQuotaRequest(): void {
  setQuotaPaywall(null);
  setQuotaRequestOpen(true);
}

export function closeQuotaRequest(): void {
  setQuotaRequestOpen(false);
}

export { quotaPaywall, quotaRequestOpen };
